import baseAPI from "@/api/base";
import {
  ADD_PARTICIPANT_TO_CALL_URL,
  GET_TWILIO_TOKEN_URL,
  PAUSE_RECORDING_URL,
  RESUME_RECORDING_URL,
  SEARCH_TWILIO_NUMBERS_URL,
  TRANSFER_CALL_URL,
} from "@/utils/urls";
import { Call, Device } from "@twilio/voice-sdk";
import { AxiosResponse } from "axios";

const DEVICE_RETRY_MAX = 3;
const DEVICE_RETRY_DELAY_MS = 50;
const MAX_RE_REGISTRATION_ATTEMPTS = 3;

let device: Device | null = null;
let activeConnection: Call | null = null;
let initializationPromise: Promise<Device | null> | null = null;
let audioDevicesReady = false;

let reRegistrationAttempts = 0;
let reRegistrationResetTimer: ReturnType<typeof setTimeout> | null = null;

type IncomingCallCallback = (callSid: string | null) => void;
type IncomingConnectionCallback = (connection: Call) => void;
let onIncomingDisconnectCallback: IncomingCallCallback | null = null;
let onIncomingCancelCallback: IncomingCallCallback | null = null;
let onIncomingAcceptCallback: IncomingCallCallback | null = null;
let onIncomingConnectionCallback: IncomingConnectionCallback | null = null;

const pickDefaultDevice = <T extends { deviceId: string }>(devices: T[]): T =>
  devices.find((d) => d.deviceId === "default") ?? devices[0];

const waitForInputDevices = async (
  maxRetries: number = DEVICE_RETRY_MAX
): Promise<MediaDeviceInfo[]> => {
  if (!device?.audio) {
    throw new Error("Device audio not available");
  }

  let retries = 0;
  let inputs: MediaDeviceInfo[] = [];

  while (retries < maxRetries) {
    inputs = Array.from(device.audio.availableInputDevices.values());
    if (inputs.length > 0) break;
    await new Promise((resolve) => setTimeout(resolve, DEVICE_RETRY_DELAY_MS));
    retries++;
  }

  if (!inputs.length) {
    throw new Error(
      "No input devices available. Please check your microphone permissions and try again."
    );
  }

  return inputs;
};

const setupAudioDevices = async (): Promise<void> => {
  if (!device?.audio) {
    throw new Error("Device audio not available");
  }

  const inputs = await waitForInputDevices();
  const setupPromises: Promise<void>[] = [
    device.audio.setInputDevice(pickDefaultDevice(inputs).deviceId),
  ];

  if (device.audio.isOutputSelectionSupported) {
    const outputs = Array.from(device.audio.availableOutputDevices.values());
    if (!outputs.length) {
      throw new Error("No output devices available");
    }
    setupPromises.push(device.audio.speakerDevices.set(pickDefaultDevice(outputs).deviceId));
  }

  await Promise.all(setupPromises);
};

const establishConnection = async (
  to: string,
  userId: string,
  contactId: string,
  bucketOptionId: string,
  organizationId: string,
  enableTranscription: boolean = true,
  enableAISummary: boolean = true,
  dialerCallListContactId: string = "",
  icpId: string = ""
): Promise<Call> => {
  if (!device) {
    throw new Error("Device not initialized");
  }

  const finalEnableAISummary = enableTranscription ? enableAISummary : false;

  const connectParams: Record<string, string> = {
    To: to.replace(/\s+/g, ""),
    userId,
    contactId,
    bucketOptionId,
    organizationId,
    enableTranscription: enableTranscription.toString(),
    enableAISummary: finalEnableAISummary.toString(),
    dialerCallListContactId,
    // ICP picked for this call — the backend uses it for the AI call summary.
    icpId,
  };

  try {
    return await device.connect({ params: connectParams });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (errorMessage.includes("A call is already active")) {
      if (activeConnection) {
        try {
          activeConnection.disconnect();
        } catch (disconnectError) {
          console.warn("Error disconnecting existing call:", disconnectError);
        }
        activeConnection = null;
      }
      return await device.connect({ params: connectParams });
    }

    throw error;
  }
};

const setupCallEventListeners = (connection: Call, isIncoming: boolean = false): void => {
  const callSid = connection.parameters?.CallSid || null;

  connection.on("accept", () => {
    if (isIncoming && onIncomingAcceptCallback) {
      onIncomingAcceptCallback(connection.parameters?.CallSid || callSid);
    }
  });

  connection.on("disconnect", () => {
    const currentCallSid = connection.parameters?.CallSid || callSid;
    activeConnection = null;
    if (isIncoming && onIncomingDisconnectCallback) {
      onIncomingDisconnectCallback(currentCallSid);
    }
  });

  connection.on("reject", () => {
    activeConnection = null;
  });

  connection.on("cancel", () => {
    const currentCallSid = connection.parameters?.CallSid || callSid;
    activeConnection = null;
    if (isIncoming && onIncomingCancelCallback) {
      onIncomingCancelCallback(currentCallSid);
    }
  });

  connection.on("error", (err: unknown) => {
    // Error 31000 is a benign "General Error" Twilio fires after a call ends normally
    // (e.g. remote party disconnects). Suppress it to avoid false error toasts.
    const errCode = (err as any)?.code;
    if (errCode === 31000) {
      console.debug("[Twilio] Suppressed benign error 31000 (disconnected-by-remote).");
      return;
    }
    console.error("Call error:", err instanceof Error ? err.message : err);
    activeConnection = null;
  });
};

const fetchToken = async (identity: string): Promise<string> => {
  const resp: AxiosResponse = await baseAPI.get(GET_TWILIO_TOKEN_URL, {
    params: { identity },
    _skipCreditModal: true,
  } as any);
  return resp.data.data;
};

class Twillio {
  static initTwilio = async (identity: string): Promise<Device | null> => {
    if (device && device.identity === identity) {
      if (activeConnection) return device;
      if (device.state === "registered" || device.state === "registering") return device;
      if (device.state === "unregistered") device.register();
      return device;
    }

    if (initializationPromise) return initializationPromise;

    initializationPromise = (async () => {
      try {
        const token = await fetchToken(identity);

        device = new Device(token, {
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          edge: "roaming",
          allowIncomingWhileBusy: false,
          logLevel: 1,
        });

        if (device.audio) {
          device.audio.incoming(false);
          device.audio.outgoing(false);
          device.audio.disconnect(false);
        }

        device.on("error", async (err: any) => {
          console.error("Twilio Device error:", err);
          const errorCode = err?.code || err?.causes?.[0]?.code;
          if (![20101, 31204, 31009].includes(errorCode)) return;
          if (reRegistrationAttempts >= MAX_RE_REGISTRATION_ATTEMPTS) return;
          reRegistrationAttempts++;
          try {
            const freshToken = await fetchToken(identity);
            device!.updateToken(freshToken);
            if (device!.state === "unregistered") device!.register();
            if (reRegistrationResetTimer) clearTimeout(reRegistrationResetTimer);
            reRegistrationResetTimer = setTimeout(() => {
              reRegistrationAttempts = 0;
              reRegistrationResetTimer = null;
            }, 60000);
          } catch (recoveryErr) {
            console.error("Token recovery failed:", recoveryErr);
          }
        });

        device.on("disconnect", () => {
          activeConnection = null;
        });

        device.on("registered", () => {
          reRegistrationAttempts = 0;
          if (reRegistrationResetTimer) {
            clearTimeout(reRegistrationResetTimer);
            reRegistrationResetTimer = null;
          }
        });

        device.on("unregistered", async () => {
          if (reRegistrationAttempts >= MAX_RE_REGISTRATION_ATTEMPTS) return;
          reRegistrationAttempts++;
          try {
            const freshToken = await fetchToken(identity);
            device!.updateToken(freshToken);
            if (device!.state === "unregistered") device!.register();
            if (reRegistrationResetTimer) clearTimeout(reRegistrationResetTimer);
            reRegistrationResetTimer = setTimeout(() => {
              reRegistrationAttempts = 0;
              reRegistrationResetTimer = null;
            }, 60000);
          } catch (err) {
            console.error("Auto re-registration failed:", err);
          }
        });

        device.on("tokenWillExpire", async () => {
          try {
            const newToken = await fetchToken(identity);
            device!.updateToken(newToken);
          } catch (err) {
            console.error("Failed to refresh token:", err);
          }
        });

        device.on("incoming", (connection: Call) => {
          activeConnection = connection;
          setupCallEventListeners(connection, true);
          if (onIncomingConnectionCallback) {
            onIncomingConnectionCallback(connection);
          }
        });

        device.on("cancel", () => {
          activeConnection = null;
        });

        device.on("reject", () => {
          activeConnection = null;
        });

        device.register();

        setupAudioDevices()
          .then(() => {
            audioDevicesReady = true;
          })
          .catch(console.warn);

        return device;
      } catch (err: any) {
        if (err?._handled) return null;
        console.error("Failed to initialize Twilio Device:", err);
        throw err;
      }
    })();

    initializationPromise.finally(() => {
      initializationPromise = null;
    });

    return initializationPromise;
  };

  static makeCall = async (
    to: string,
    userId: string,
    contactId: string,
    bucketOptionId: string,
    organizationId: string,
    enableTranscription: boolean = true,
    enableAISummary: boolean = true,
    dialerCallListContactId: string = "",
    icpId: string = ""
  ): Promise<{ call: Call; callSid: string | undefined }> => {
    if (!device) throw new Error("Device not initialized");
    if (!device.audio) throw new Error("Device audio not available");
    if (activeConnection) throw new Error("A call is already active");

    if (!audioDevicesReady) {
      await setupAudioDevices();
    }

    activeConnection = await establishConnection(
      to,
      userId,
      contactId,
      bucketOptionId,
      organizationId,
      enableTranscription,
      enableAISummary,
      dialerCallListContactId,
      icpId
    );

    setupCallEventListeners(activeConnection);

    const params = activeConnection.parameters || {};
    const customParams = (activeConnection as any).customParameters || {};
    const callSid: string | undefined =
      params.CallSid ||
      params.callSid ||
      customParams.CallSid ||
      customParams.callSid ||
      params.TempCallSid ||
      params.tempCallSid ||
      params.temp_call_sid;

    return { call: activeConnection, callSid };
  };

  static sendDigits = (digits: string): void => {
    if (!activeConnection) throw new Error("No active call");
    activeConnection.sendDigits(digits);
  };

  static answerIncomingCall = async (): Promise<Call> => {
    if (!activeConnection) throw new Error("No incoming call to answer");
    activeConnection.accept();
    return activeConnection;
  };

  static getActiveCallSid = (): string | null => {
    return activeConnection?.parameters?.CallSid || null;
  };

  static hasActiveCall = (): boolean => {
    return !!activeConnection;
  };

  static getActiveCallStatus = (): string | null => {
    return activeConnection?.status() || null;
  };

  static rejectIncomingCall = async (): Promise<void> => {
    if (!activeConnection) throw new Error("No incoming call to reject");
    activeConnection.reject();
    activeConnection = null;
  };

  static hangup = async (): Promise<void> => {
    if (activeConnection) {
      try {
        activeConnection.disconnect();
      } catch (error) {
        console.warn("Error disconnecting call:", error);
      } finally {
        activeConnection = null;
      }
    }
  };

  static forceDisconnect = async (): Promise<void> => {
    if (activeConnection) {
      try {
        activeConnection.disconnect();
      } catch (error) {
        console.warn("Error force disconnecting call:", error);
      } finally {
        activeConnection = null;
      }
    }
  };

  static getCallState = (): { hasActiveConnection: boolean; deviceReady: boolean } => {
    return {
      hasActiveConnection: !!activeConnection,
      deviceReady: !!device,
    };
  };

  static resetCallState = (): void => {
    activeConnection = null;
  };

  static addParticipant = async (phoneNumber: string, callSid: string): Promise<unknown> => {
    if (!phoneNumber || !callSid) throw new Error("Missing phone number or CallSid");
    try {
      const response = await baseAPI.post(ADD_PARTICIPANT_TO_CALL_URL, { phoneNumber, callSid });
      return response.data;
    } catch (error) {
      console.error("Failed to add participant:", error);
      return false;
    }
  };

  static transferCall = async (callSid: string, phoneNumber: string): Promise<unknown> => {
    if (!callSid || !phoneNumber) throw new Error("Missing callSid or phoneNumber");
    try {
      const response = await baseAPI.post(TRANSFER_CALL_URL, { callSid, phoneNumber });
      return response.data;
    } catch (error) {
      console.error("Failed to transfer call:", error);
      throw error;
    }
  };

  static muteCall = (): void => {
    if (!activeConnection) throw new Error("No active call");
    activeConnection.mute(true);
  };

  static unmuteCall = (): void => {
    if (!activeConnection) throw new Error("No active call");
    activeConnection.mute(false);
  };

  static searchNumbers = async (filters: Record<string, unknown>): Promise<AxiosResponse> => {
    return await baseAPI.get(SEARCH_TWILIO_NUMBERS_URL, { params: filters });
  };

  static checkMicrophonePermission = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch {
      return false;
    }
  };

  static getMicrophones = (): MediaDeviceInfo[] => {
    if (!device?.audio) throw new Error("Device not ready");
    return Array.from(device.audio.availableInputDevices.values());
  };

  static setMicrophone = async (deviceId: string): Promise<void> => {
    if (!device?.audio) throw new Error("Device not ready");
    await device.audio.setInputDevice(deviceId);
  };

  static setSpeaker = async (deviceId: string): Promise<void> => {
    if (!device) throw new Error("Device not ready");
    const audio = device.audio;
    if (!audio?.isOutputSelectionSupported) return;
    await audio.speakerDevices.set(deviceId);
  };

  static getSpeakers = (): MediaDeviceInfo[] => {
    if (!device?.audio?.isOutputSelectionSupported) return [];
    return Array.from(device.audio.availableOutputDevices.values());
  };

  static pauseRecording = async (callSid: string): Promise<unknown> => {
    if (!callSid) throw new Error("Missing callSid");
    const response = await baseAPI.post(PAUSE_RECORDING_URL, { callSid });
    return response.data;
  };

  static resumeRecording = async (callSid: string): Promise<unknown> => {
    if (!callSid) throw new Error("Missing callSid");
    const response = await baseAPI.post(RESUME_RECORDING_URL, { callSid });
    return response.data;
  };

  static onRinging = (cb: () => void): void => {
    device?.on("ringing", () => cb());
  };

  static onConnect = (cb: () => void): void => {
    device?.on("connect", () => cb());
  };

  static onDisconnect = (cb: () => void): void => {
    device?.on("disconnect", () => cb());
  };

  static onError = (cb: (err: unknown) => void): void => {
    device?.on("error", (err) => cb(err));
  };

  static getDevice = (): Device | null => device;

  static getDeviceState = (): {
    hasDevice: boolean;
    state: string | null;
    identity: string | null;
  } => {
    if (!device) return { hasDevice: false, state: null, identity: null };
    return { hasDevice: true, state: device.state, identity: device.identity || null };
  };

  static getActiveConnection = (): Call | null => activeConnection;

  static onIncomingCallDisconnect = (callback: IncomingCallCallback | null): void => {
    onIncomingDisconnectCallback = callback;
  };

  static onIncomingCallCancel = (callback: IncomingCallCallback | null): void => {
    onIncomingCancelCallback = callback;
  };

  static onIncomingCallAccept = (callback: IncomingCallCallback | null): void => {
    onIncomingAcceptCallback = callback;
  };

  static onIncomingConnection = (callback: IncomingConnectionCallback | null): void => {
    onIncomingConnectionCallback = callback;
  };

  static ensureRegistered = async (
    identity: string,
    timeoutMs: number = 5000
  ): Promise<boolean> => {
    if (!device) {
      try {
        await Twillio.initTwilio(identity);
      } catch {
        return false;
      }
    }

    if (!device) return false;
    if (device.state === "registered") return true;

    try {
      const freshToken = await fetchToken(identity);
      device.updateToken(freshToken);
      if (device.state === "unregistered") device.register();
    } catch {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      if (device!.state === "registered") {
        resolve(true);
        return;
      }

      const timeout = setTimeout(() => resolve(false), timeoutMs);
      device!.once("registered", () => {
        clearTimeout(timeout);
        resolve(true);
      });
    });
  };

  static clearIncomingCallCallbacks = (): void => {
    onIncomingDisconnectCallback = null;
    onIncomingCancelCallback = null;
    onIncomingAcceptCallback = null;
    onIncomingConnectionCallback = null;
  };
}

export default Twillio;
